import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import '../styles/Admin.css';

interface City {
  _id: string;
  name: string;
  additionalPrice: number;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

function CityManagement() {
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    additionalPrice: 0,
    isDefault: false,
    isActive: true
  });

  // استخدام API_BASE_URL مع API_ENDPOINTS.CITIES

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    try {
      const token = localStorage.getItem('fotomista_token');
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CITIES}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCities(data.cities);
      } else {
        console.error('Failed to fetch cities');
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('fotomista_token');
      const url = editingCity 
        ? `${API_BASE_URL}${API_ENDPOINTS.CITIES}/${editingCity._id}`
        : `${API_BASE_URL}${API_ENDPOINTS.CITIES}`;
      
      const method = editingCity ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        fetchCities();
        resetForm();
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'حدث خطأ');
      }
    } catch (error) {
      console.error('Error saving city:', error);
      alert('حدث خطأ في الاتصال');
    }
  };

  const handleDelete = async (cityId: string) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه المدينة؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('fotomista_token');
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CITIES}/${cityId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        fetchCities();
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'حدث خطأ في الحذف');
      }
    } catch (error) {
      console.error('Error deleting city:', error);
      alert('حدث خطأ في الاتصال');
    }
  };

  const handleSetDefault = async (cityId: string) => {
    try {
      const token = localStorage.getItem('fotomista_token');
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CITIES}/${cityId}/set-default`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        fetchCities();
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'حدث خطأ');
      }
    } catch (error) {
      console.error('Error setting default city:', error);
      alert('حدث خطأ في الاتصال');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      additionalPrice: 0,
      isDefault: false,
      isActive: true
    });
    setEditingCity(null);
    setShowAddForm(false);
  };

  const startEdit = (city: City) => {
    setFormData({
      name: city.name,
      additionalPrice: city.additionalPrice,
      isDefault: city.isDefault,
      isActive: city.isActive
    });
    setEditingCity(city);
    setShowAddForm(true);
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading">جاري التحميل...</div>
      </div>
    );
  }

  return (
    <AdminLayout title="إدارة المدن">
      <motion.div 
        className="cities-overview"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="section-header">
          <h1>إدارة المدن والأسعار</h1>
          <p>إضافة وتعديل المدن وأسعارها الإضافية</p>
        </div>

        <div className="actions-bar">
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(true)}
          >
            إضافة مدينة جديدة
          </button>
        </div>

      {showAddForm && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div 
            className="modal-content"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
          >
            <div className="modal-header">
              <h2>{editingCity ? 'تعديل المدينة' : 'إضافة مدينة جديدة'}</h2>
              <button 
                className="close-btn"
                onClick={resetForm}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="city-form">
              <div className="form-group">
                <label>اسم المدينة:</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="مثال: الرياض"
                />
              </div>

              <div className="form-group">
                <label>السعر الإضافي (ريال):</label>
                <input
                  type="number"
                  min="0"
                  value={formData.additionalPrice}
                  onChange={(e) => setFormData({...formData, additionalPrice: Number(e.target.value)})}
                  required
                  placeholder="0"
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({...formData, isDefault: e.target.checked})}
                  />
                  مدينة افتراضية
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  />
                  نشطة
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingCity ? 'تحديث' : 'إضافة'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={resetForm}
                >
                  إلغاء
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}

      <div className="cities-table-container">
        <table className="cities-table">
          <thead>
            <tr>
              <th>اسم المدينة</th>
              <th>السعر الإضافي</th>
              <th>افتراضية</th>
              <th>الحالة</th>
              <th>تاريخ الإنشاء</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {cities.map((city) => (
              <tr key={city._id} className={city.isDefault ? 'default-city' : ''}>
                <td>{city.name}</td>
                <td>
                  {city.additionalPrice === 0 ? 'مجاناً' : `${city.additionalPrice} ريال`}
                </td>
                <td>
                  {city.isDefault ? (
                    <span className="badge badge-primary">افتراضية</span>
                  ) : (
                    <button 
                      className="btn btn-sm btn-outline"
                      onClick={() => handleSetDefault(city._id)}
                    >
                      تعيين كافتراضية
                    </button>
                  )}
                </td>
                <td>
                  <span className={`badge ${city.isActive ? 'badge-success' : 'badge-danger'}`}>
                    {city.isActive ? 'نشطة' : 'معطلة'}
                  </span>
                </td>
                <td>
                  {new Date(city.createdAt).toLocaleDateString('en-US')}
                </td>
                <td className="actions">
                  <button 
                    className="btn btn-sm btn-primary"
                    onClick={() => startEdit(city)}
                  >
                    تعديل
                  </button>
                  {!city.isDefault && (
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(city._id)}
                    >
                      حذف
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {cities.length === 0 && (
          <div className="empty-state">
            <p>لا توجد مدن مضافة بعد</p>
            <button 
              className="btn btn-primary"
              onClick={() => setShowAddForm(true)}
            >
              إضافة أول مدينة
            </button>
          </div>
        )}
      </div>

      <div className="info-box">
        <h3>معلومات مهمة:</h3>
        <ul>
          <li>المدينة الافتراضية لا تحتوي على رسوم إضافية</li>
          <li>يمكن أن تكون هناك مدينة افتراضية واحدة فقط</li>
          <li>لا يمكن حذف المدينة الافتراضية</li>
          <li>المدن المعطلة لن تظهر في نموذج الحجز</li>
        </ul>
      </div>
      </motion.div>
    </AdminLayout>
  );
}

export default CityManagement;